import { useState, useEffect } from 'react';
import { NETWORK_CONFIG } from '../config/constants';

/**
 * ネットワーク状態を監視するカスタムフック
 * @returns {Object} ネットワーク状態の情報
 */
export const useNetworkStatus = () => {
  const [networkInfo, setNetworkInfo] = useState({
    isSlowConnection: false,
    effectiveType: 'unknown',
    downlink: 0,
    rtt: 0,
    saveData: false,
    isOnline: navigator.onLine,
  });

  useEffect(() => {
    // Network Information APIが利用可能かチェック
    if (
      !navigator.connection &&
      !navigator.mozConnection &&
      !navigator.webkitConnection
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Network Information API not supported');
      }
      return;
    }

    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    const updateNetworkInfo = () => {
      const effectiveType = connection.effectiveType || 'unknown';
      const downlink = connection.downlink || 0;
      const rtt = connection.rtt || 0;
      const saveData = connection.saveData || false;

      // 低速ネットワークの判定
      const isSlowConnection =
        NETWORK_CONFIG.DISABLE_VIDEO_TYPES.includes(effectiveType) ||
        downlink < NETWORK_CONFIG.SLOW_CONNECTION_THRESHOLD ||
        rtt > NETWORK_CONFIG.SLOW_RTT_THRESHOLD ||
        saveData;

      setNetworkInfo({
        isSlowConnection,
        effectiveType,
        downlink,
        rtt,
        saveData,
        isOnline: navigator.onLine,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('Network status updated:', {
          effectiveType,
          downlink: `${downlink} Mbps`,
          rtt: `${rtt} ms`,
          saveData,
          isSlowConnection,
          threshold: {
            downlink: NETWORK_CONFIG.SLOW_CONNECTION_THRESHOLD,
            rtt: NETWORK_CONFIG.SLOW_RTT_THRESHOLD,
            disableTypes: NETWORK_CONFIG.DISABLE_VIDEO_TYPES,
          },
        });
      }
    };

    // 初期状態を設定
    updateNetworkInfo();

    // ネットワーク状態の変更を監視
    const handleConnectionChange = () => {
      updateNetworkInfo();
    };

    const handleOnline = () => {
      setNetworkInfo(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setNetworkInfo(prev => ({ ...prev, isOnline: false }));
    };

    // イベントリスナーを追加
    connection.addEventListener('change', handleConnectionChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      // イベントリスナーを削除
      connection.removeEventListener('change', handleConnectionChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return networkInfo;
};
